/* Validate a real Shopify discount; never substitute a client-side price for a charge. */
(function (global) {
  'use strict';
  function error(code, uncertain = false) { const failure = new Error(global.Norelvi?.offerDiscount?.message || 'Das Angebot ist gerade nicht verfügbar. Bitte versuche es später erneut.'); failure.code = code; failure.cartMayHaveChanged = uncertain; return failure; }
  function rootPath(value) { if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || /[\\?#\s]/.test(value)) throw error('invalid_route'); return value.replace(/\/?$/, '/'); }
  function validSettings(config) { return Boolean(config && Number.isInteger(config.percent) && config.percent >= 0 && config.percent <= 50 && (config.percent === 0 || config.configured === true)); }
  function settings(config) { if (!validSettings(config)) throw error('discount_not_configured'); return config; }
  function isB(item) { return item.properties?._norelvi_offer === 'b'; }
  function signature(item) {
    return JSON.stringify([String(item.variant_id || item.id), item.selling_plan_allocation?.selling_plan?.id || null, Object.entries(item.properties || {}).sort(([a],[b]) => a.localeCompare(b))]);
  }
  // Match the configured native rule: Shopify truncates the discount per unit.
  function discountedUnit(original, percent = 10) { return original - Math.floor(original * percent / 100); }
  function isGrouped(item) { return isB(item) || (item.properties?._norelvi_offer === 'a' && Boolean(item.properties?._norelvi_offer_recipe)); }
  function recipeFor(item) {
    const raw = item.properties?._norelvi_offer_recipe;
    let recipe;
    try { recipe = raw ? JSON.parse(raw) : isB(item) ? [[Number(item.variant_id || item.id), 2]] : null; } catch { return null; }
    if (!Array.isArray(recipe) || !recipe.length || recipe.length > 3) return null;
    const ids = new Set();
    for (const part of recipe) {
      if (!Array.isArray(part) || part.length !== 2 || !Number.isSafeInteger(part[0]) || part[0] <= 0 || !Number.isSafeInteger(part[1]) || part[1] < 1 || part[1] > 30 || ids.has(part[0])) return null;
      ids.add(part[0]);
    }
    return recipe;
  }
  function quantityStep(item) {
    const part = recipeFor(item)?.find(value => String(value[0]) === String(item.variant_id || item.id));
    if (!part) return null;
    if (item.properties?._norelvi_offer_recipe && Number(item.properties._norelvi_offer_unit_qty) !== part[1]) return null;
    return part[1];
  }
  function groupedLines(cart, item) {
    const token = item.properties?._norelvi_offer_token;
    return token ? cart.items.filter(line => line.properties?._norelvi_offer_token === token) : [item];
  }
  function validGroup(cart, item) {
    const recipe = recipeFor(item), lines = groupedLines(cart, item);
    if (!recipe || lines.length !== recipe.length) return false;
    if (item.properties?._norelvi_offer_recipe && !item.properties?._norelvi_offer_token) return false;
    let multiplier;
    return recipe.every(([variant, base]) => {
      const line = lines.find(candidate => String(candidate.variant_id || candidate.id) === String(variant));
      if (!line || line.properties?._norelvi_offer !== item.properties?._norelvi_offer || JSON.stringify(recipeFor(line)) !== JSON.stringify(recipe) || quantityStep(line) !== base || !Number.isSafeInteger(line.quantity) || line.quantity < base || line.quantity % base !== 0) return false;
      const current = line.quantity / base;
      if (multiplier === undefined) multiplier = current;
      return current === multiplier;
    });
  }
  function quantityUpdates(cart, key, newQuantity) {
    if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(newQuantity) || newQuantity < 0) throw error('invalid_quantity');
    const item = cart.items.find(line => line.key === key);
    if (!item || !isGrouped(item)) throw error('invalid_offer');
    const lines = groupedLines(cart, item);
    if (newQuantity === 0) return Object.fromEntries(lines.map(line => [line.key, 0]));
    const step = quantityStep(item);
    if (!step || newQuantity % step !== 0 || !validGroup(cart, item)) throw error('invalid_quantity');
    const multiplier = newQuantity / step;
    const updates = {};
    for (const line of lines) {
      const quantity = quantityStep(line) * multiplier;
      const rule = line.quantity_rule || {};
      if (!Number.isSafeInteger(quantity) || quantity < (rule.min || 1) || (rule.max && quantity > rule.max) || (quantity - (rule.min || 1)) % (rule.increment || 1) !== 0) throw error('invalid_quantity');
      updates[line.key] = quantity;
    }
    return updates;
  }
  function groupItems(items, expected, offer, token) {
    if (!['a','b'].includes(offer) || typeof token !== 'string' || !token || !Array.isArray(items) || !Array.isArray(expected) || !expected.length || expected.length > 3 || items.length !== expected.length) throw error('invalid_offer');
    const recipe = expected.map(item => [Number(item.id), item.baseQuantity ?? (offer === 'b' ? 2 : item.quantity)]);
    const recipeString = JSON.stringify(recipe);
    let multiplier;
    const grouped = items.map(item => {
      const wanted = expected.find(value => String(value.id) === String(item.id));
      const base = recipe.find(value => String(value[0]) === String(item.id))?.[1];
      if (!wanted || wanted.quantity !== item.quantity || !Number.isSafeInteger(item.quantity) || !Number.isSafeInteger(base) || base < 1 || base > 30 || item.quantity < base || item.quantity % base !== 0) throw error('invalid_offer');
      const current = item.quantity / base;
      if (multiplier === undefined) multiplier = current;
      if (current !== multiplier) throw error('invalid_offer');
      const result = { id: item.id, quantity: item.quantity, properties: { _norelvi_offer: offer, _norelvi_offer_token: token, _norelvi_offer_recipe: recipeString, _norelvi_offer_unit_qty: String(base) } };
      if (!recipeFor(result)) throw error('invalid_offer');
      return result;
    });
    return grouped;
  }
  function validCart(cart, config) {
    if (!cart || !Array.isArray(cart.items)) return false;
    for (const item of cart.items.filter(isGrouped)) {
      if (!validGroup(cart, item)) return false;
      if (isB(item)) {
        if (!validSettings(config)) return false;
        const unit = discountedUnit(item.original_price, config.percent);
        if (!Number.isSafeInteger(item.original_price) || item.original_price <= 0 || item.final_price !== unit || item.final_line_price !== unit * item.quantity) return false;
      }
    }
    return true;
  }
  function codes(cart) {
    const values = new Set((cart.discount_codes || []).map(value => value.code).filter(Boolean));
    const applications = [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(value => value.discount_application))];
    applications.forEach(application => { if (application?.type === 'discount_code' && application.title) values.add(application.title); });
    return [...values];
  }
  function existingUnchanged(before, after, token, eligible = [], config = null) {
    const previous = new Map(before.items.map(item => [signature(item), item]));
    const remaining = after.items.filter(item => item.properties?._norelvi_offer_token !== token);
    return remaining.length === previous.size && remaining.every(item => {
      const prior = previous.get(signature(item));
      if (!prior || prior.quantity !== item.quantity || prior.original_price !== item.original_price || prior.original_line_price !== item.original_line_price) return false;
      if (prior.final_price === item.final_price && prior.final_line_price === item.final_line_price) return true;
      // A native quantity discount also reprices existing units of the selected variant.
      // Only its verified automatic price reduction is allowed; rollback stays exact.
      if (!validSettings(config) || config.percent <= 0 || config.code || item.final_line_price >= prior.final_line_price) return false;
      const selected = eligible.find(value => String(value.id) === String(item.variant_id || item.id));
      if (!selected || selected.originalPrice !== item.original_price || !Number.isSafeInteger(item.quantity) || item.quantity < 1) return false;
      const unit = discountedUnit(item.original_price, config.percent);
      return Number.isSafeInteger(item.original_price) && item.original_price > 0 &&
        item.original_line_price === item.original_price * item.quantity &&
        selected.unitPrice === unit && item.final_price === unit && item.final_line_price === unit * item.quantity;
    });
  }
  function validateAdded(before, after, expected, token, config) {
    if (!validCart(after, config) || !existingUnchanged(before, after, token, expected, config)) return false;
    const added = after.items.filter(item => item.properties?._norelvi_offer_token === token);
    if (added.length !== expected.length) return false;
    return expected.every(wanted => {
      const line = added.find(item => String(item.variant_id || item.id) === String(wanted.id));
      return line && line.quantity === wanted.quantity && line.original_price === wanted.originalPrice &&
        line.final_price === wanted.unitPrice && line.final_line_price === wanted.unitPrice * wanted.quantity;
    });
  }
  async function purchase({ request, root, config, items, expected, token }) {
    settings(config);
    const base = rootPath(root);
    if (!token || !Array.isArray(expected) || !expected.length || items.length !== expected.length || expected.some(item => !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.unitPrice !== discountedUnit(item.originalPrice, config.percent))) throw error('invalid_offer');
    const groupedItems = groupItems(items, expected, 'b', token);
    const before = await request(base + 'cart.js');
    if (!validCart(before, config)) throw error('existing_offer_invalid');
    const previousCodes = codes(before);
    const offerCode = config.percent > 0 ? config.code : '';
    const selectedCodes = [...new Set([...previousCodes, ...(offerCode ? [offerCode] : [])])];
    let attempted = false;
    try {
      attempted = true;
      await request(base + 'cart/add.js', { items: groupedItems });
      const cart = offerCode ? await request(base + 'cart/update.js', { discount: selectedCodes.join(',') }) : await request(base + 'cart.js');
      if (!validateAdded(before, cart, expected, token, config)) throw error('discount_unverified');
      return cart;
    } catch (failure) {
      if (!attempted) throw failure;
      let restored = false;
      try {
        let current = await request(base + 'cart.js');
        const owned = current.items.filter(item => item.properties?._norelvi_offer_token === token);
        if (owned.length) await request(base + 'cart/update.js', { updates: Object.fromEntries(owned.map(item => [item.key, 0])) });
        // Preserve every pre-existing code; do not replace other shoppers' discount choices.
        if (offerCode && !previousCodes.some(code => code.toLowerCase() === offerCode.toLowerCase())) {
          const latest = await request(base + 'cart.js');
          const keepCodes = [...new Set([...previousCodes, ...codes(latest).filter(code => code.toLowerCase() !== offerCode.toLowerCase())])];
          await request(base + 'cart/update.js', { discount: keepCodes.join(',') });
        }
        current = await request(base + 'cart.js');
        restored = existingUnchanged(before, current, token) && !current.items.some(item => item.properties?._norelvi_offer_token === token) && validCart(current, config);
      } catch (_) { restored = false; }
      throw error(failure.code || 'discount_failed', !restored);
    }
  }
  const api = { isB, isGrouped, quantityStep, quantityUpdates, groupItems, validCart, discountedUnit, purchase, validateAdded, existingUnchanged, codes };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  global.NorelviOfferDiscount = api;
})(typeof window === 'undefined' ? globalThis : window);
